import * as actionTypes from "./actionTypes";
import orderService from "services/orderService";
import voucherService from "services/voucherService";
import paymentService from "services/paymentService";
import campaignRateService from "services/campaignRateService";
import timeUtils from "utils/time";
import { CHECKOUT_OUTCOME, mapCheckoutError } from "utils/checkoutError";
import { ERROR_CODES, serverIdOf } from "utils/userError";
import { orderPlacedMessage } from "utils/orderConfirmation";
import { resolvePaymentFailure } from "utils/paymentFailure";
import { normalizeVoucherDiscounts } from "utils/vouchers";
import {
  createCheckoutRequestKey,
  createGuest,
  isGuestPolicyEnabled,
  recipientSubtitle
} from "../../config/guestOrdering";

// A response came back from the server (not a lost request or a 5xx whose outcome is unknown).
export const hasServerResponse = error =>
  !!error &&
  !error.isNetworkError &&
  error.name !== "TypeError" &&
  !(error.response && error.response.status >= 500);

// Your own orders carry a checkoutRequestKey too, so a retry after a lost response
// returns the order already placed instead of placing it twice.
let ownCheckoutKey = null;

const withCheckoutKey = order => {
  if (!order || order.checkoutRequestKey || order.guestOrder) return order;
  if (!ownCheckoutKey) ownCheckoutKey = createCheckoutRequestKey();
  return { ...order, checkoutRequestKey: ownCheckoutKey };
};

// One order request at a time: a second tap on Place Order while the first is in
// flight is ignored.
let orderInFlight = false;

const oneOrderAtATime = send => {
  if (orderInFlight) return Promise.resolve();
  orderInFlight = true;
  const release = () => {
    orderInFlight = false;
  };
  let sending;
  try {
    sending = Promise.resolve(send());
  } catch (error) {
    sending = Promise.reject(error);
  }
  return sending.then(
    result => {
      release();
      return result;
    },
    error => {
      release();
      throw error;
    }
  );
};

// A checkoutRequestKey (yours or a guest's) is reused only when retrying a request whose
// response never arrived. Once the server answers, the next attempt gets a new key.
const markGuestKey = (dispatch, order, error) => {
  if (!order || !order.checkoutRequestKey) return;
  if (error !== undefined && !hasServerResponse(error)) return;
  if (order.checkoutRequestKey === ownCheckoutKey) {
    ownCheckoutKey = null;
    return;
  }
  dispatch({
    type: actionTypes.GUEST_CHECKOUT_KEY_SPENT,
    requestKey: order.checkoutRequestKey
  });
};

const menuResetState = () => {
  return {
    type: actionTypes.MENU_RESET_STATE
  };
};

export const cartResetState = () => dispatch => {
  return dispatch({
    type: actionTypes.CART_RESET_STATE
  });
};

const checkoutResetState = () => {
  return {
    type: actionTypes.CHECKOUT_RESET_STATE
  };
};

const cartSetPayInfo = payInfo => {
  return {
    type: actionTypes.CART_SET_PAY_INFO,
    payload: payInfo
  };
};

export const cartSetRestaurantOpen = payInfo => {
  return {
    type: actionTypes.CART_UPDATED_RESTAURANT_STATUS,
    payload: payInfo
  };
};

const discountOperationIsPercentage = voucher =>
  voucher.discountOperation === "%";

const maxBasketCostIsPresent = voucher => !!voucher.maxBasketCost;

export const calculateSubsidy = (idRestaurant, subtotal, taxDelivery, tipValue) => {

  return dispatch => {
    voucherService
      .calculateSubsidy({ idRestaurant: idRestaurant, value: subtotal })
      .then(data => {
        dispatch(cartSetRestaurantOpen(data.isBusinessCorporate))
        
          const discounts = data.discounts;
          const voucherWithMaxBasketCost =
          data.discounts.find(
            voucher =>
              discountOperationIsPercentage(voucher) &&
              maxBasketCostIsPresent(voucher)
          ) || {};
          if (subtotal !== data.newValue) {
            const subsidy = subtotal.toFixed(2) - data.newValue;
            const total = data.newValue + taxDelivery;
            console.log("subtotal if: ", subtotal)
            dispatch(
              appliedCartValues(
                subtotal,
                subsidy.toFixed(2),
                total,
                voucherWithMaxBasketCost.maxBasketCost,
                discounts,
                tipValue
              )
            );
          } else {
            const total = subtotal + taxDelivery;
            console.log("subtotal else: ", subtotal)
            dispatch(
              appliedCartValues(
                subtotal,
                0,
                total,
                voucherWithMaxBasketCost.maxBasketCost,
                discounts,
                tipValue
              )
            );
          }
        
        
      })
      .catch(err => {
        console.log(err);
      });
  };
};

export const fetchingCards = () => {
  return dispatch => {
    paymentService
      .getCards()
      .then(data => {
        dispatch(successFetchingCarts(data));
      })
      .catch(err => {
        console.log(err);
      });
  };
};

export const fetchingCampaignRate = idBusiness => {
  return dispatch => {
    campaignRateService
      .getCampaignRate(idBusiness)
      .then(campaignRates => {
        if (
          !campaignRates.find(
            campaignRate =>
              campaignRate.chargeType === "T" && campaignRate.status === "E"
          )
        ) {
          campaignRates.push({
            chargeType: "T",
            statusReg: "A",
            status: "E",
            amount: 10,
            discountType: "P"
          });
        }
        dispatch(successFetchingCampaignRate(campaignRates));
      })
      .catch(err => {
        console.log(err);
      });
  };
};

export const placeOrder = (order, payInfo) => {
  return dispatch => oneOrderAtATime(() => {
    order = withCheckoutKey(order);
    return orderService
      .placeOrder(order)
      .then(data => {
        let payInfoUpdated = {
          ...payInfo,
          idOrder: data.id,
          publicOrderNumber: data.publicOrderNumber
        };
        markGuestKey(dispatch, order);
        dispatch(cartSetPayInfo(payInfoUpdated));
        dispatch(successPlaceOrder(data));
        dispatch(openModalPlacedOrderOnCart());
        dispatch(consumeVoucher(payInfoUpdated));
        // Voice ordering reads the placed order from the promise.
        return data;
      })
      .catch(err => {
        markGuestKey(dispatch, order, err);
        dispatch(triggerModalErrorPlacedOrder(err));
      });
  });
};

export const placeOrderAndPay = (order, payInfo) => {

  let amout = payInfo.tipValue + payInfo.amount;

  payInfo.amount = amout;
  return dispatch => oneOrderAtATime(() => {
    order = withCheckoutKey(order);
    return orderService
      .placeOrder(order)
      .then(data => {
        let payInfoUpdated = {
          ...payInfo,
          idOrder: data.id,
          publicOrderNumber: data.publicOrderNumber
        };
        markGuestKey(dispatch, order);
        dispatch(successPlaceOrder(data));
        dispatch(cartSetPayInfo(payInfoUpdated));
        dispatch(paymentOrder(payInfoUpdated));
      })
      .catch(err => {
        markGuestKey(dispatch, order, err);
        dispatch(triggerModalErrorPlacedOrder(err));
      });
  });
};

export const placeOrderAndGoToAddNewCard = (order, payInfo, history) => {

  return dispatch => oneOrderAtATime(() => {
    order = withCheckoutKey(order);
    return orderService
      .placeOrder(order)
      .then(data => {
        const payInfoUpdated = {
          ...payInfo,
          idOrder: data.id,
          publicOrderNumber: data.publicOrderNumber
        };
        markGuestKey(dispatch, order);
        dispatch(successPlaceOrder(data));
        dispatch(cartSetPayInfo(payInfoUpdated));
        history.push("/Menu/checkout");
      })
      .catch(err => {
        markGuestKey(dispatch, order, err);
        dispatch(triggerModalErrorPlacedOrder(err));
      });
  });
};

const reconcilePaymentFailure = (payload, error) => dispatch => {
  return resolvePaymentFailure(payload, error).then(checkoutError => {
    dispatch({
      type: actionTypes.TRIGGER_MODAL_ERROR_PLACED_ORDER,
      checkoutError,
    });
  });
};

export const paymentOrder = payload => {
  return dispatch => {
    return paymentService
      .pay(payload)
      // Handle the payment rejection in the same step so an error raised while
      // showing the result cannot trigger a second reconciliation.
      .then(data => {
        if (data.success) {
          dispatch(openModalPlacedOrderOnCart());
          dispatch(consumeVoucher(payload));
        } else {
          return dispatch(reconcilePaymentFailure(payload, data));
        }
      }, err => {
        return dispatch(reconcilePaymentFailure(payload, err));
      });
  };
};

export const consumeVoucher = payload => {
  return dispatch => {
    const vouchers = normalizeVoucherDiscounts(payload && payload.discounts);
    if (vouchers.length === 0) {
      return Promise.resolve();
    }

    const confirmVoucher = payload.payType === "A";
    const voucher = {
      idOrder: payload.idOrder,
      totalOrder: payload.amount,
      confirmVoucher: confirmVoucher,
      vouchers: vouchers
    };
    return voucherService
      .consume(voucher)
      .catch(err => {
        dispatch(triggerModalErrorPlacedOrder(err, {
          outcome: CHECKOUT_OUTCOME.CONFIRMED,
          orderNumber: payload.publicOrderNumber,
          postOrderFailure: true,
        }));
      });
  };
};


export const toggleCart = (status = null) => {
  return {
    type: actionTypes.TOGGLE_CART,
    status: status
  };
};

// Straight into the basket. payload.guestId (optional) says who the item is for;
// without it an item keeps its own guestId (the basket's + button).
export const addProductToCartNow = payload => {
  return {
    type: actionTypes.ADD_PRODUCT_TO_CART,
    payload: payload
  };
};

const isDeliveringAtHome = state => {
  const addresses = state.menuReducer && state.menuReducer.addresses;
  return !!addresses && !!addresses[0] && addresses[0].idCustomerDeliveryAddress != null;
};

// Every Add to basket: asks "Who's this for?" when the employee can order for guests,
// otherwise adds straight away exactly as before. Guest orders are for corporate
// delivery and collection only, so home delivery never asks.
export const addProductToCart = payload => (dispatch, getState) => {
  const state = getState();
  const policy = state.cartReducer.cart.guestPolicy;
  if (!isGuestPolicyEnabled(policy) || isDeliveringAtHome(state)) {
    return dispatch(addProductToCartNow(payload));
  }
  return dispatch({
    type: actionTypes.GUEST_RECIPIENT_ASK,
    payload: payload,
    subtitle: recipientSubtitle(payload)
  });
};

// The sheet's Add to basket: guestId for someone already in the basket, newGuest
// ({firstName, lastName}, already checked) for a new one, neither for Me.
export const chooseGuestRecipient = ({ guestId, newGuest } = {}) => (dispatch, getState) => {
  const prompt = getState().cartReducer.cart.guestPrompt;
  if (!prompt) return;
  let recipientId = guestId || null;
  if (newGuest) {
    const guest = createGuest(newGuest.firstName, newGuest.lastName);
    dispatch({ type: actionTypes.GUEST_ADDED_TO_BASKET, guest });
    recipientId = guest.id;
  }
  dispatch({ type: actionTypes.GUEST_RECIPIENT_CHOSEN, guestId: recipientId });
  dispatch(addProductToCartNow({ ...prompt.payload, guestId: recipientId }));
};

// Closing the sheet adds nothing.
export const cancelGuestRecipient = () => ({
  type: actionTypes.GUEST_RECIPIENT_CANCEL
});

// GET /order/v1/order/guest-policy at sign-in and whenever the basket opens. Any failure
// (including an order service without the endpoint) means no guest ordering.
export const fetchGuestPolicy = () => dispatch =>
  orderService
    .getGuestPolicy()
    .then(policy =>
      dispatch({
        type: actionTypes.GUEST_POLICY_LOADED,
        policy: isGuestPolicyEnabled(policy) ? policy : null
      })
    )
    .catch(() =>
      dispatch({ type: actionTypes.GUEST_POLICY_LOADED, policy: null })
    );

// One person's order values from calculate-discount, worked out like calculateSubsidy.
export const partyValueFromDiscount = (party, data, taxDelivery) => {
  const subtotal = party.subtotal;
  const discounts = (data && data.discounts) || [];
  const voucherWithMaxBasketCost =
    discounts.find(
      voucher => discountOperationIsPercentage(voucher) && maxBasketCostIsPresent(voucher)
    ) || {};
  const subsidy =
    data && subtotal !== data.newValue ? Number(subtotal.toFixed(2)) - data.newValue : 0;
  return {
    guestId: party.guestId,
    subtotal,
    subsidy: Number(subsidy.toFixed(2)),
    total: (subsidy ? data.newValue : subtotal) + taxDelivery,
    discounts,
    maxBasketCost: voucherWithMaxBasketCost.maxBasketCost
  };
};

// Several people in the basket: the subsidy is worked out per order, by asking
// calculate-discount for each person's items. The totals block shows the sums.
export const calculatePartySubsidies = (
  idRestaurant,
  parties,
  taxDelivery,
  tipValue,
  withSubsidy
) => dispatch => {
  const valueFor = party =>
    withSubsidy
      ? voucherService
          .calculateSubsidy({ idRestaurant: idRestaurant, value: party.subtotal })
          .then(data => {
            dispatch(cartSetRestaurantOpen(data.isBusinessCorporate));
            return partyValueFromDiscount(party, data, taxDelivery);
          })
      : Promise.resolve(partyValueFromDiscount(party, null, 0));
  return Promise.all(parties.map(valueFor))
    .then(values => {
      dispatch({ type: actionTypes.CART_PARTY_VALUES, partyValues: values });
      const sum = field => values.reduce((total, value) => total + value[field], 0);
      const withMax = values.find(value => !!value.maxBasketCost) || {};
      dispatch(
        appliedCartValues(
          sum("subtotal"),
          sum("subsidy").toFixed(2),
          sum("total"),
          withMax.maxBasketCost,
          values.length > 0 ? values[0].discounts : [],
          tipValue
        )
      );
      return values;
    })
    .catch(err => {
      console.log(err);
      return null;
    });
};

export const clearPartyValues = () => ({
  type: actionTypes.CART_PARTY_VALUES,
  partyValues: []
});

// The order API's 4xx messages are written for customers ("Daily guest limit reached"); anything
// else is described here, never in the server's words.
const failureReason = error => {
  if (!hasServerResponse(error)) return "no answer from Lunch.Team, so we couldn't confirm it";
  if (error && error.code === "AUTHENTICATION_REQUIRED") return "your session expired";
  const data = error && error.data;
  return (
    (data && (data.message || data.errorMessage)) ||
    // A 400 body is thrown as it is; an Error's message is the request layer's, not the server's.
    (error && !(error instanceof Error) && (error.message || error.errorMessage || error.description)) ||
    "we couldn't place it"
  );
};

const consumeQuietly = payInfo =>
  voucherService
    .consume({
      idOrder: payInfo.idOrder,
      totalOrder: payInfo.amount,
      confirmVoucher: payInfo.payType === "A" && (payInfo.discounts || []).length > 0,
      vouchers: payInfo.discounts
    })
    .catch(err => {
      console.log(err);
    });

// Several people: one order each (yours without guest fields, each guest's with their
// name and own checkoutRequestKey), sent one after another and stopped at the first
// failure. A saved card pays each order in turn. sends: [{guestId, name, order, payInfo}].
export const placePartyOrders = sends => dispatch =>
  oneOrderAtATime(() => sendPartyOrders(sends.map(send => ({ ...send, order: withCheckoutKey(send.order) })), dispatch));

const sendPartyOrders = async (sends, dispatch) => {
  const result = { placed: [], failedName: null, failureMessage: null, notSent: [] };
  const placedGuestIds = [];

  for (let index = 0; index < sends.length; index += 1) {
    const send = sends[index];
    const fail = (message, code, error) => {
      result.failedName = send.name;
      result.failureMessage = message;
      result.failureCode = code;
      result.failureAt = new Date();
      result.failureServerId = serverIdOf(error);
      result.notSent = sends.slice(index + 1).map(next => next.name);
    };

    let data;
    try {
      data = await orderService.placeOrder(send.order);
    } catch (error) {
      markGuestKey(dispatch, send.order, error);
      fail(
        failureReason(error),
        hasServerResponse(error) ? ERROR_CODES.ORDER_PLACE_FAILED : ERROR_CODES.ORDER_UNKNOWN_STATE,
        error
      );
      break;
    }
    markGuestKey(dispatch, send.order);
    const orderNumber = data.publicOrderNumber || data.id;
    const payInfo = {
      ...send.payInfo,
      idOrder: data.id,
      publicOrderNumber: data.publicOrderNumber
    };

    if (payInfo.payType === "C") {
      const payment = { ...payInfo, amount: payInfo.amount + (payInfo.tipValue || 0) };
      let paid = false;
      let answered = true;
      let payError = null;
      try {
        const response = await paymentService.pay(payment);
        paid = !!response && !!response.success;
      } catch (error) {
        payError = error;
        answered = hasServerResponse(error);
      }
      if (!paid) {
        // A lost answer may still have charged the card: never invite paying again.
        fail(
          answered
            ? `order ${orderNumber} was created but the card was declined, so it isn't paid`
            : `order ${orderNumber} was created but we couldn't confirm the card payment`,
          answered ? ERROR_CODES.PAYMENT_DECLINED : ERROR_CODES.PAYMENT_UNKNOWN_STATE,
          payError
        );
        break;
      }
      consumeQuietly(payment);
    } else {
      consumeQuietly(payInfo);
    }
    result.placed.push({ orderNumber, name: send.name });
    placedGuestIds.push(send.guestId || null);
  }

  const allPlaced = !result.failedName && result.notSent.length === 0;
  if (!allPlaced && placedGuestIds.length > 0) {
    dispatch({ type: actionTypes.CART_REMOVE_PARTIES, guestIds: placedGuestIds });
  }
  dispatch({ type: actionTypes.CART_BATCH_RESULT, result: { ...result, allPlaced } });
  dispatch(fetchGuestPolicy());
  return result;
};

export const closeBatchResult = () => ({
  type: actionTypes.CART_BATCH_RESULT_CLOSE
});

export const deleteProductFromCart = payload => {
  return {
    type: actionTypes.DELETE_PRODUCT_FROM_CART,
    payload: payload
  };
};

export const deleteAllProductsFromCart = () => {
  return {
    type: actionTypes.DELETE_ALL_PRODUCTS_FROM_CART
  };
};

export const appliedCartValues = (subtotal, subsidy, total, maxBasketCost, discounts, tipValue) => {
  return {
    type: actionTypes.CART_CALCULATED_VALUES,
    subtotal: subtotal,
    subsidy: subsidy,
    tipValue: tipValue,
    total: total,
    maxBasketCost: maxBasketCost,
    discounts: discounts
  };
};

export const successFetchingCarts = cards => {
  return {
    type: actionTypes.SUCCESS_FETCHING_CARDS,
    cards: cards
  };
};

export const successFetchingCampaignRate = data => {
  return {
    type: actionTypes.SUCCESS_FETCHING_CAMPAIGN_RATE,
    campaignRate: data
  };
};

export const triggerModalErrorPlacedOrder = (error, context) => {
  return {
    type: actionTypes.TRIGGER_MODAL_ERROR_PLACED_ORDER,
    checkoutError: mapCheckoutError(error, context)
  };
};

export const successPlaceOrder = data => {
  return {
    type: actionTypes.SUCCESS_PLACE_ORDER,
    payload: {
      idOrder: data.id,
      publicOrderNumber: data.publicOrderNumber,
      orderDeliveryDate: timeUtils.dateArrayToDate(data.orderDeliveryDate),
      isInvoice: false,
      descriptionSuccess: orderPlacedMessage(data.publicOrderNumber)
    }
  };
};

export const closeModalErrorPlacedOrder = () => dispatch => {
  dispatch({ type: actionTypes.CLOSE_MODAL_ERROR_PLACED_ORDER });
  dispatch(toggleIsButtonDisabled(false));
};

export const openModalPlacedOrderOnCart = () => {
  return {
    type: actionTypes.CART_TOGGLE_SHOW_MODAL_PLACE_ORDER,
    payload: true
  };
};

export const closeModalPlacedOrderOnCart = history => dispatch => {
  dispatch(menuResetState());
  dispatch(cartResetState());
  dispatch(checkoutResetState());
  dispatch(toggleIsButtonDisabled(false));
  history.push("/Menu");
};

export const toggleIsButtonDisabled = disabled => {
  return {
    type: actionTypes.CART_SET_PLACED_BUTTON_DISABLED,
    payload: disabled
  };
};

export const openModalReceiptOnCart = () => dispatch => {
  dispatch({
    type: actionTypes.CART_TOGGLE_SHOW_MODAL_PLACE_ORDER,
    payload: false
  });
  dispatch({
    type: actionTypes.CART_TOGGLE_SHOW_MODAL_RECEIPT,
    payload: true
  });
};

export const closeModalReceiptOnCart = () => dispatch => {
  dispatch(menuResetState());
  dispatch(cartResetState());
  dispatch(checkoutResetState());
  dispatch(toggleIsButtonDisabled(false));
};

//////////////////////////////// Invoice //////////////////////////////////////

export const handlePayInvoice = (payload, history) => {
  return dispatch => {
    return paymentService
      .pay(payload)
      .then(data => {
        if (data.success) {
          dispatch(payInvoiceSuccess(data.description));
          dispatch(toggleCart(false));
          dispatch(openModalPlacedOrderOnCart());
          history.push("/Menu");
        } else {
          dispatch(triggerModalErrorPlacedOrder(data, {
            paymentOutcomeUnknown: true,
          }));
        }
      })
      .catch(error => {
        dispatch(triggerModalErrorPlacedOrder(error, {
          paymentOutcomeUnknown: true,
        }));
      });
  };
};

const payInvoiceSuccess = descriptionSucess => dispatch => {
  dispatch({
    type: actionTypes.INVOICE_PAY_SUCCESS,
    descriptionSuccess: descriptionSucess
  });
};

//////////////////////////////// Card //////////////////////////////////////

export const deleteCard = cardId => {
  return dispatch => {
    paymentService.deleteCard(cardId).then(result => {
      dispatch(fetchingCards());
    });
  };
};

export const changeDeliveryTomorrow = payload => dispatch => {
  dispatch({
    type: actionTypes.CART_DELIVERY_TOMORROW,
    payload: payload
  });
};



// WEBPACK FOOTER //
// ./src/store/actions/Cart.js